import { Heading } from "@components/atoms/Heading"
import { Button } from "@components/atoms/Button"
import { useLatestNews } from "@hooks/queries"
import { tv } from "@utils/tw"
import { Col, Container, Grid } from "@components/layout/Grid"
import { Card } from "@components/molecules/Cards"
import { EditableText } from "eddev/blocks"
import { ArticleCardFragment } from "../../types.graphql"
import { useIsMobile } from "@hooks/useViewportDerived"
import { useHydrating } from "eddev/routing"

export const LatestNewsPanel = ({
  articles,
  link,
  headerStyle,
  viewAllStyle,
}: {
  articles: ArticleCardFragment[]
  link: string
  headerStyle: "subtitle" | "regular" | "hide"
  viewAllStyle: "none" | "light" | "dark"
}) => {
  const isMobile = useIsMobile()

  const hydrating = useHydrating()
  if (hydrating) return null

  return (
    <>
      {articles && (
        <>
          <Container className={latestNewsContainerStyles()}>
            <Grid>
              {headerStyle === "subtitle" && (
                <>
                  {!isMobile && <Col initial={12} tablet={1} laptop={2} />}
                  <Col initial={12} tablet={10} laptop={8} className="flex items-center justify-center">
                    <div className="flex flex-col items-center gap-5 py-6 w-full [&_span]:type-label-caps [&_span]:text-mid-grey [&_h4]:type-title-s [&_h4]:my-0 [&_h4]:!text-center">
                      <EditableText
                        as="span"
                        store="subtitle"
                        className="!text-center"
                        placeholder="Enter subtitle..."
                      />
                      <EditableText as="h4" store="title" className="!text-center" placeholder="Enter title..." />
                    </div>
                  </Col>
                  {!isMobile && <Col initial={12} tablet={1} laptop={2} />}
                </>
              )}

              {headerStyle === "regular" && (
                <>
                  <Col initial={8} className="flex items-center justify-center">
                    <EditableText as="h5" store="title" placeholder="Enter title..." />
                  </Col>
                  <Col initial={4} className="justify-end">
                    <Button color="white" size="small" href={link}>
                      View all
                    </Button>
                  </Col>
                </>
              )}
            </Grid>

            <Grid>
              {headerStyle === "subtitle" || headerStyle === "hide" ? (
                <>
                  {articles.slice(0, 3).map((article, i) => (
                    <Col initial={12} tablet={6} laptop={4} key={i}>
                      <Card type="news" {...article} />
                    </Col>
                  ))}
                </>
              ) : (
                <>
                  {articles.map((article, i) => (
                    <Col initial={12} tablet={6} laptop={4} key={i}>
                      <Card type="news" {...article} />
                    </Col>
                  ))}
                </>
              )}
            </Grid>
          </Container>

          {viewAllStyle !== "none" && (
            <Container className={browseAllContainerStyles({ light: viewAllStyle === "light" ? true : false })}>
              <Grid>
                <Col initial={12} tablet={8} className="flex items-center justify-center">
                  <Heading as="h5">
                    <EditableText
                      store="view_all"
                      defaultValue="Browse all articles in our news archive"
                      placeholder="Browse all articles in our news archive"
                    />
                  </Heading>
                </Col>
                <Col initial={12} tablet={4}>
                  <Button
                    href="/newsroom/news-archive"
                    color={headerStyle ? "white" : "outlineReversed"}
                    size="medium"
                    icon={headerStyle ? undefined : "ARROW_RIGHT"}
                    iconStyle={headerStyle ? undefined : "right"}
                  >
                    {headerStyle ? "View All" : "News Archive"}
                  </Button>
                </Col>
              </Grid>
            </Container>
          )}
        </>
      )}
    </>
  )
}

const latestNewsContainerStyles = tv({
  base: [
    "my-4",
    "[&>.--grid:nth-of-type(1)>div]:flex [&>.--grid:nth-of-type(1)>div]:items-center [&>.--grid:nth-of-type(1)>div]:py-4",
    "[&>.--grid:nth-of-type(2)]:gap-y-5 [&>.--grid:nth-of-type(2)>div]:flex [&>.--grid:nth-of-type(2)>div]:items-stretch",
    "wp:[&>.--grid:nth-of-type(1)]:gap-3 wp:[&>.--grid:nth-of-type(2)]:gap-3",
  ],
})

const browseAllContainerStyles = tv({
  base: [
    "bg-black rounded-md my-7",
    "[&_h5]:text-white [&_h5]:!text-center",
    "[&_.--grid>div]:flex [&_.--grid>div]:items-center [&_.--grid>div]:justify-center [&_.--grid>div]:p-5",
    "[&_.--grid>div:nth-of-type(2)]:p-[0_var(--spacing-5)_var(--spacing-5)_var(--spacing-5)]",
    "md:[&_h5]:text-left",
    "md:[&_.--grid>div]:justify-start",
    "md:[&_.--grid>div:nth-of-type(2)]:pt-5 md:[&_.--grid>div:nth-of-type(2)]:justify-end",
  ],
  variants: {
    light: {
      true: "bg-light-grey [&_h5]:text-black",
    },
  },
})
